import React, { useState, useEffect } from "react"
import { Col, Row, TabPane, FormGroup, Label, Input } from "reactstrap"

const RepairTypeTab = ({ nextTab, request, setRequest }) => {
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedAlloys, setSelectedAlloys] = useState({
    Reballing: "",
    Retinning: "",
    "Alloy Conversion": "",
  })
  const [selectedShippingTypes, setSelectedShippingTypes] = useState([])
  const [returnPartsType, setReturnPartsType] = useState("")
  const [customReturnType, setCustomReturnType] = useState("")

  const handleTypeChange = (type, isChecked) => {
    if (isChecked) {
      setSelectedTypes([...selectedTypes, type])
    } else {
      setSelectedTypes(selectedTypes.filter(t => t !== type))
      // Clear the selected alloy when deselecting the main type
      if (["Reballing", "Retinning", "Alloy Conversion"].includes(type)) {
        setSelectedAlloys(prev => ({ ...prev, [type]: "" }))
      }
      // Clear return parts type if deselecting return parts
      if (type === "Return parts in Tape/Reel or Trays") {
        setReturnPartsType("")
        setCustomReturnType("")
      }
    }
  }

  const handleAlloyChange = (mainType, alloy) => {
    setSelectedAlloys(prev => ({ ...prev, [mainType]: alloy }))
  }

  const handleShippingTypeChange = (type, isChecked) => {
    if (isChecked) {
      setSelectedShippingTypes([...selectedShippingTypes, type])
    } else {
      setSelectedShippingTypes(selectedShippingTypes.filter(t => t !== type))
    }
  }

  const handleReturnPartsTypeChange = type => {
    setReturnPartsType(type)
    if (type !== "Other") {
      setCustomReturnType("")
    }
  }

  const isCategorySelected = options => {
    return options.some(option => selectedTypes.includes(option.id))
  }

  const handleNext = () => {
    const alloyTypes = ["Reballing", "Retinning", "Alloy Conversion"]
    const selectedAlloyTypes = alloyTypes
      .filter(type => selectedTypes.includes(type))
      .map(type => `${type} (${selectedAlloys[type]})`)

    // Add shipping types to the request
    const shippingTypesString =
      selectedShippingTypes.length > 0
        ? `Shipped parts packed: ${selectedShippingTypes.join(", ")}`
        : ""

    // Add return parts type to the request
    const returnPartsString = selectedTypes.includes(
      "Return parts in Tape/Reel or Trays"
    )
      ? `Return parts in: ${
          returnPartsType === "Other" ? customReturnType : returnPartsType
        }`
      : ""

    setRequest({
      ...request,
      repairOptions: [
        ...selectedTypes.filter(
          t =>
            !alloyTypes.includes(t) &&
            t !== "Return parts in Tape/Reel or Trays"
        ),
        ...selectedAlloyTypes,
        shippingTypesString,
        returnPartsString,
      ]
        .filter(Boolean)
        .join(";"),
    })
    nextTab(1)
  }

  const alloyOptions = [
    "Leaded Alloy (Sn63Pb37)",
    "Lead Free Alloy (SAC305)",
    "Unverified / Not Applicable",
  ]

  const shippingOptions = [
    "Tape&Reel",
    "Tube",
    "Tray",
    "Bag",
    "Loose",
    "ESD box",
    "undefined",
  ]

  const returnPartsOptions = ["Tape&Reel", "Tray", "Other"]

  const repairTypes = [
    {
      category: "Recovery",
      options: [
        {
          id: "Board is non-functional and can be scrapped",
          label: "Board is non-functional and can be scrapped",
        },
        {
          id: "Board is non-functional, return to customer",
          label: "Board is non-functional, return to customer",
        },
        {
          id: "Board is functional, return to customer",
          label: "Board is functional, return to customer",
        },
        {
          id: "Board is functional and has batteries/sensitive components",
          label: "Board is functional and has batteries/sensitive components",
        },
        {
          id: "Board is conformally coated",
          label: "Board is conformally coated",
        },
        {
          id: "The orientation of the device needs to change",
          label: "The orientation of the device needs to change",
        },
      ],
    },
    {
      id: "Reballing",
      label: "Reballing",
      hasAlloy: true,
    },
    {
      id: "Retinning",
      label: "Retinning",
      hasAlloy: true,
    },
    {
      id: "Alloy Conversion",
      label: "Alloy Conversion",
      hasAlloy: true,
    },
    { id: "Test", label: "Test" },
    { id: "PCB Repair / Rework", label: "PCB Repair / Rework" },
    {
      id: "Shipped parts packed",
      label: "Shipped parts packed",
      hasShippingOptions: true,
    },
    {
      id: "Return parts in Tape/Reel or Trays",
      label: "Return parts in Tape/Reel or Trays",
      hasReturnOptions: true,
    },
    {
      id: "Component Marking required",
      label: "Component Marking required (Dots, labels, Etching etc.)",
    },
    {
      id: "Other",
      label: "Other (Please specify in the Project Description Field)",
    },
    {
      id: "Certificate of Conformity (CoC)",
      label: "Certificate of Conformity (CoC)",
    },
  ]

  return (
    <TabPane tabId={1}>
      <Row>
        <Col lg="12">
          <h5 className="font-size-14 mb-4">Repair Types</h5>
          <p>Please select one or more repair types:</p>

          <div className="mt-3">
            {repairTypes.map(type => (
              <div key={type.id || type.category}>
                {type.options ? (
                  <>
                    <FormGroup check className="mb-2">
                      <Input
                        type="checkbox"
                        id={`category-${type.category}`}
                        checked={isCategorySelected(type.options)}
                        style={{ pointerEvents: "none" }}
                        className="category-checkbox"
                      />
                      <Label check for={`category-${type.category}`}>
                        <h6 className="mb-0">{type.category}</h6>
                      </Label>
                    </FormGroup>
                    {type.options.map(option => (
                      <FormGroup check key={option.id} className="ms-3 mb-1">
                        <Input
                          type="checkbox"
                          id={option.id}
                          checked={selectedTypes.includes(option.id)}
                          onChange={e =>
                            handleTypeChange(option.id, e.target.checked)
                          }
                        />
                        <Label check for={option.id}>
                          {option.label}
                        </Label>
                      </FormGroup>
                    ))}
                  </>
                ) : (
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      id={type.id}
                      checked={selectedTypes.includes(type.id)}
                      onChange={e =>
                        handleTypeChange(type.id, e.target.checked)
                      }
                    />
                    <Label check for={type.id}>
                      {type.label}
                    </Label>
                    {type.hasAlloy && selectedTypes.includes(type.id) && (
                      <div className="ms-3 mt-2">
                        {alloyOptions.map(alloy => (
                          <FormGroup check key={alloy} className="mb-1">
                            <Input
                              type="radio"
                              id={`${type.id}-${alloy}`}
                              name={type.id}
                              checked={selectedAlloys[type.id] === alloy}
                              onChange={() => handleAlloyChange(type.id, alloy)}
                            />
                            <Label check for={`${type.id}-${alloy}`}>
                              {alloy}
                            </Label>
                          </FormGroup>
                        ))}
                      </div>
                    )}
                    {type.hasShippingOptions &&
                      selectedTypes.includes(type.id) && (
                        <div className="ms-3 mt-2">
                          {shippingOptions.map(option => (
                            <FormGroup check key={option} className="mb-1">
                              <Input
                                type="checkbox"
                                id={`shipping-${option}`}
                                checked={selectedShippingTypes.includes(option)}
                                onChange={e =>
                                  handleShippingTypeChange(
                                    option,
                                    e.target.checked
                                  )
                                }
                              />
                              <Label check for={`shipping-${option}`}>
                                {option}
                              </Label>
                            </FormGroup>
                          ))}
                        </div>
                      )}
                    {type.hasReturnOptions &&
                      selectedTypes.includes(type.id) && (
                        <div className="ms-3 mt-2">
                          {returnPartsOptions.map(option => (
                            <FormGroup check key={option} className="mb-1">
                              <Input
                                type="radio"
                                id={`return-${option}`}
                                name="returnParts"
                                checked={returnPartsType === option}
                                onChange={() =>
                                  handleReturnPartsTypeChange(option)
                                }
                              />
                              <Label check for={`return-${option}`}>
                                {option}
                              </Label>
                            </FormGroup>
                          ))}
                          {returnPartsType === "Other" && (
                            <FormGroup className="ms-4 mt-2">
                              <Input
                                type="text"
                                value={customReturnType}
                                onChange={e =>
                                  setCustomReturnType(e.target.value)
                                }
                                placeholder="Please specify return type"
                              />
                            </FormGroup>
                          )}
                        </div>
                      )}
                  </FormGroup>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>
      <div className="d-flex justify-content-end mt-4">
        <button
          className="btn btn-primary"
          type="button"
          disabled={
            selectedTypes.length === 0 ||
            ["Reballing", "Retinning", "Alloy Conversion"].some(
              type => selectedTypes.includes(type) && !selectedAlloys[type]
            ) ||
            (selectedTypes.includes("Return parts in Tape/Reel or Trays") &&
              (!returnPartsType ||
                (returnPartsType === "Other" && !customReturnType)))
          }
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </TabPane>
  )
}

export default RepairTypeTab
